
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "./layout/MainLayout";
import {
  Activity,
  Users,
  Folder,
  Home,
  Settings,
} from "lucide-react";

import { PageHeader } from "./functions/PageHeader";
import { LoadingState } from "./functions/LoadingState";
import Toast from "./common/Toast";
import Card from "./common/Card";
import { AuthError } from "./common/AuthError";
import { FeatureCard } from "./common/FeatureCard";
import { ActivityReport } from "./common/ActivityReport";

const FeaturesSection = () => {
  const navigate = useNavigate();

  const features = [
    {
      title: "Project Management",
      description: "Create new projects or edit existing ones with comprehensive project lifecycle management tools.",
      path: "project-management",
      icon: Folder,
      gradient: "bg-gradient-to-br from-blue-600 to-indigo-600",
      delay: 0
    },
    {
      title: "User Management",
      description: "Create and manage user accounts with dedicated credentials and role-based access control.",
      path: "user-management",
      icon: Users,
      gradient: "bg-gradient-to-br from-indigo-600 to-purple-600",
      delay: 100
    },
    {
      title: "Project Execution",
      description: "Select and execute existing platform or digital twin viewer projects with advanced monitoring.",
      path: "user-projects?mode=execute",
      icon: Activity,
      gradient: "bg-gradient-to-br from-purple-600 to-pink-600",
      delay: 200
    }
  ];

  return (
    <Card
      variant="gradient"
      title="Platform Features"
      subtitle="Manage and monitor your platform"
      icon={Settings}
      gradient="from-blue-600 to-indigo-600"
      className="mb-8"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {features.map((feature) => (
          <FeatureCard
            key={feature.path}
            title={feature.title}
            description={feature.description}
            onClick={() => navigate(`/${feature.path}`)}
            icon={feature.icon}
            gradient={feature.gradient}
            delay={feature.delay}
          />
        ))}
      </div>
    </Card>
  );
};

const PlatformFeatures = () => {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("token");
      
      if (!token) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      try {
        setIsAuthenticated(true);
      } catch (error) {
        console.error("Auth check failed:", error);
        setIsAuthenticated(false);
        localStorage.removeItem("token");
        localStorage.removeItem("userID");
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
  };

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
          <LoadingState />
        </div>
      </MainLayout>
    );
  }

  if (!isAuthenticated) {
    return (
      <MainLayout>
        <AuthError />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="max-w-7xl mx-auto">
          
          <PageHeader
            icon={Home}
            title="Digital Twin Platform"
            subtitle="Comprehensive platform for managing digital twin projects, users, and system activities"
          />

          <FeaturesSection />
          <ActivityReport autoRefresh={true} />
          {toast && (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => setToast(null)}
            />
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default PlatformFeatures;